// Standing PER-LIFT photos (Session AID): the lift's own visual log -- servicing context, parts
// condition, progression over time -- kept on the Asset record, deliberately SEPARATE from BOTH the
// per-visit service photos (a service record shows THAT visit's photos) and the site's general
// reference photos (Session ADE). On a multi-lift site each lift's gallery is independent, and
// nothing here touches Site.photos. Same experience as the site gallery: drag files from the
// desktop to upload (click-to-browse stays), drag thumbnails to reorder (persisted; the array is
// the order), and a two-step per-photo delete. Reuses the shared JobPhotoGallery with the lift
// photo proxy.
import { Loader } from "../../components/Loader";
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { api, ApiError } from "../../lib/api";
import { dragHasFiles } from "../../lib/photoDrag";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { isImageFile, prepareImagesForUpload } from "../../lib/imageUpload";

export function LiftPhotos({ assetId }: { assetId: string }) {
  const [count, setCount] = useState(0);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.assets.liftPhotoCount(assetId);
      setCount(r.count);
    } catch {
      setError("Could not load the lift photos.");
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(selected: File[]) {
    if (selected.length === 0) return;
    const images = selected.filter(isImageFile);
    if (images.length === 0) {
      setError("Only image files can be uploaded.");
      return;
    }
    setBusy(true);
    setError(images.length < selected.length ? "Only the image files were uploaded (other file types are skipped)." : "");
    try {
      const r = await api.assets.uploadLiftPhotos(assetId, await prepareImagesForUpload(images));
      setCount(r.count);
      setVersion((v) => v + 1);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? "Google Drive is not connected, so photos cannot be uploaded yet."
          : err instanceof ApiError && err.status === 415
            ? "Only image files can be uploaded."
            : err instanceof ApiError && err.status === 413
              ? "A photo was too large to upload."
              : "Could not upload the photo. Please try again.",
      );
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    void upload(Array.from(e.target.files ?? []));
  }

  async function deletePhoto(index: number) {
    setError("");
    try {
      const r = await api.assets.deleteLiftPhoto(assetId, index);
      setCount(r.count);
      setVersion((v) => v + 1);
    } catch {
      setError("Could not delete the photo. Please try again.");
    }
  }

  async function reorder(order: number[]) {
    setError("");
    try {
      const r = await api.assets.reorderLiftPhotos(assetId, order);
      setCount(r.count);
      setVersion((v) => v + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("The photos changed while you were reordering, so the order was not saved. The view has been refreshed; please try again.");
        setVersion((v) => v + 1);
      } else {
        setError("Could not save the new photo order. Please try again.");
      }
    }
  }

  return (
    <div
      className={`rounded-ele border-2 border-dashed p-2 transition-colors ${dragOver ? "border-ele-green bg-ele-green/5" : "border-transparent"}`}
      onDragEnter={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault(); // required, or the browser navigates to the dropped file
      }}
      onDragLeave={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        if (!busy) void upload(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} disabled={busy} id={`lift-photo-input-${assetId}`} className="hidden" />
        <label
          htmlFor={`lift-photo-input-${assetId}`}
          className={`inline-flex cursor-pointer items-center gap-2 rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset ${busy ? "pointer-events-none opacity-50" : ""}`}
        >
          {busy ? "Uploading..." : "Add photos"}
        </label>
        <span className="ele-desktop-only text-xs text-ele-body opacity-70">{dragOver ? "Drop the photos to upload them" : "or drag photos here from your desktop"}</span>
        {count > 0 && <span className="text-xs text-ele-body opacity-70">{count} on file</span>}
      </div>
      <p className="mt-1 text-xs text-ele-body opacity-70">
        This lift&apos;s own standing photos: servicing context, the condition of parts, and how it changes over time. Photos from a service visit stay on that visit&apos;s record.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-xs text-ele-danger">
          {error}
        </p>
      )}
      {loading ? (
        <Loader shape="inline" className="mt-3" />
      ) : (
        <JobPhotoGallery
          jobId={assetId}
          count={count}
          label=""
          emptyText="No lift photos yet."
          version={version}
          buildPhotoUrl={(i) => api.assets.liftPhotoUrl(assetId, i)}
          altLabel={(i) => `Lift photo ${i + 1}`}
          onDeletePhoto={deletePhoto}
          onReorder={(order) => void reorder(order)}
        />
      )}
    </div>
  );
}
